import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';

import { Table, Th, Td, Tr } from 'common/components/organisms/DataTable';
import { GhostBtn } from 'common/components/molecules/ListToolbar';
import {
  ArrowRight, CheckCircle, AlertTriangle, Download
} from 'common/images/FaIcons';
import ImportStepIndicator from './ImportStepIndicator';
import FieldFilter from './FieldFilter';
import ChangeValue from './ChangeValue';
import {
  changeKey, changeMatchesFilter, changesToCsv, columnLabel, countByField
} from './helpers';
import { MAX_ROWS_SHOWN, REFORMATTED_FILTER } from '../../machineCsv.constants';
import {
  Body, Muted, Callout, Footer, FooterActions, TableScroll, Pill, Stats, Stat, StatValue,
  FilterPills, ArrowCell, PrimaryAction, WarnPill
} from './MachineCsv.styled';

function ImportPreviewStep({
  preview, fileName, applying, onApply, onChooseAnother
}) {
  const { t } = useTranslation();
  const [field, setField] = useState(null);
  const { changes, summary } = preview;
  const downloadChanges = useCallback(() => {
    const blob = new Blob([changesToCsv(t, changes)], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${(fileName || 'assets').replace(/\.csv$/i, '')}-changes.csv`);
  }, [t, changes, fileName]);
  const counts = countByField(changes);
  const reformatted = changes.filter(change => change.reformatted).length;
  const shown = changes.filter(change => changeMatchesFilter(change, field));
  const ignored = [
    ...(preview.read_only_columns || []).map(key => columnLabel(t, key)),
    ...(preview.unknown_columns || [])
  ];

  return (
    <Body>
      <Stats>
        <Stat>
          <StatValue accent>{summary.machines_changed}</StatValue>
          {t('assets.csv.preview.machinesChanged', { defaultValue: 'machines will change' })}
        </Stat>
        <Stat>
          <StatValue>{summary.changes}</StatValue>
          {t('assets.csv.preview.changes', { defaultValue: 'changes' })}
        </Stat>
        <Stat>
          <StatValue>{summary.unchanged_rows}</StatValue>
          {t('assets.csv.preview.unchanged', { defaultValue: 'rows unchanged, skipped' })}
        </Stat>
      </Stats>
      {changes.length === 0 ? (
        <Callout>
          <CheckCircle />
          {t('assets.csv.preview.nothingToChange', {
            defaultValue: 'Nothing to change: every row already matches the site.'
          })}
        </Callout>
      ) : (
        <>
          {reformatted > 0 && (
            <Callout tone="warn" role="status">
              <AlertTriangle />
              {t('assets.csv.preview.reformattedWarning', {
                count: reformatted,
                defaultValue: '{{count}} changes look like spreadsheet auto-formatting, not edits (for example leading zeros lost, or a value turned into a date). Check them below before applying.'
              })}
            </Callout>
          )}
          <FilterPills>
            <FieldFilter
              label={t('assets.csv.preview.all', { count: changes.length, defaultValue: 'All ({{count}})' })}
              selected={!field}
              onSelect={setField}
            />
            {Object.keys(counts).map(key => (
              <FieldFilter
                key={key}
                field={key}
                label={`${columnLabel(t, key)} (${counts[key]})`}
                selected={field === key}
                onSelect={setField}
              />
            ))}
            {reformatted > 0 && (
              <FieldFilter
                field={REFORMATTED_FILTER}
                label={t('assets.csv.preview.reformattedFilter', {
                  count: reformatted, defaultValue: 'Check these ({{count}})'
                })}
                selected={field === REFORMATTED_FILTER}
                onSelect={setField}
              />
            )}
          </FilterPills>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{columnLabel(t, 'id')}</Th>
                  <Th>{t('assets.csv.preview.machine', { defaultValue: 'Machine' })}</Th>
                  <Th>{t('assets.csv.preview.field', { defaultValue: 'Field' })}</Th>
                  <Th>{t('assets.csv.preview.now', { defaultValue: 'Now' })}</Th>
                  <Th aria-hidden="true" />
                  <Th>{t('assets.csv.preview.after', { defaultValue: 'After import' })}</Th>
                </tr>
              </thead>
              <tbody>
                {shown.slice(0, MAX_ROWS_SHOWN).map(change => (
                  <Tr key={changeKey(change)}>
                    <Td>{change.machine_id}</Td>
                    <Td>{change.machine_name}</Td>
                    <Td><Pill>{columnLabel(t, change.field)}</Pill></Td>
                    <Td><ChangeValue field={change.field} value={change.old} /></Td>
                    <Td><ArrowCell><ArrowRight /></ArrowCell></Td>
                    <Td>
                      <ChangeValue field={change.field} value={change.new} after />
                      {change.reformatted && (
                        <WarnPill>{t('assets.csv.preview.reformatted', { defaultValue: 'Check' })}</WarnPill>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
          {shown.length > MAX_ROWS_SHOWN && (
            <Muted>
              {t('assets.csv.preview.notShown', {
                count: shown.length - MAX_ROWS_SHOWN,
                defaultValue: '{{count}} more changes are not listed here; they are applied too. Download the changes CSV to see them all.'
              })}
            </Muted>
          )}
        </>
      )}
      <Callout>
        <CheckCircle />
        <div>
          {ignored.length > 0 && (
            <>
              {t('assets.csv.preview.ignoredColumns', {
                columns: ignored.join(', '),
                defaultValue: 'These columns can not be changed by an import and were ignored: {{columns}}.'
              })}
              {' '}
            </>
          )}
          {t('assets.csv.preview.confirmNote', {
            defaultValue: 'Nothing is saved until you press Apply. If someone changes one of these machines before then, the import stops and tells you which.'
          })}
        </div>
      </Callout>
      <Footer>
        <ImportStepIndicator current="preview" />
        <FooterActions>
          {changes.length > 0 && (
            <GhostBtn type="button" onClick={downloadChanges}>
              <Download />
              {t('assets.csv.preview.download', { defaultValue: 'Changes CSV' })}
            </GhostBtn>
          )}
          <GhostBtn type="button" onClick={onChooseAnother} disabled={applying}>
            {t('assets.csv.preview.chooseAnother', { defaultValue: 'Choose another file' })}
          </GhostBtn>
          {changes.length > 0 && (
            <PrimaryAction type="button" onClick={onApply} disabled={applying}>
              {applying
                ? t('assets.csv.preview.applying', { defaultValue: 'Applying...' })
                : t('assets.csv.preview.apply', { count: changes.length, defaultValue: 'Apply {{count}} changes' })}
            </PrimaryAction>
          )}
        </FooterActions>
      </Footer>
    </Body>
  );
}

ImportPreviewStep.propTypes = {
  preview: PropTypes.shape({
    changes: PropTypes.arrayOf(PropTypes.shape({
      machine_id: PropTypes.number.isRequired,
      machine_name: PropTypes.string,
      field: PropTypes.string.isRequired,
      reformatted: PropTypes.bool
    })).isRequired,
    summary: PropTypes.shape({
      machines_changed: PropTypes.number,
      changes: PropTypes.number,
      unchanged_rows: PropTypes.number
    }).isRequired,
    read_only_columns: PropTypes.arrayOf(PropTypes.string),
    unknown_columns: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  fileName: PropTypes.string,
  applying: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onChooseAnother: PropTypes.func.isRequired
};

ImportPreviewStep.defaultProps = {
  fileName: null
};

export default ImportPreviewStep;
